import React, { useCallback, useState, useEffect } from 'react';
import API from '../services/api';
import { useNavigate } from 'react-router-dom';

function CreateTask() {
  const [activeTab, setActiveTab] = useState('createTask');
  const [taskName, setTaskName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [createMessage, setCreateMessage] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locationPage, setLocationPage] = useState(1);
  const locationsPerPage = 10;

  const [locationSearchTerm, setLocationSearchTerm] = useState(''); 
  const [tasks, setTasks] = useState([]);
  const [taskSearchTerm, setTaskSearchTerm] = useState('');
  const [taskPage, setTaskPage] = useState(1);
  const tasksPerPage = 10;
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingTaskName, setEditingTaskName] = useState('');
  const [editingTaskStatus, setEditingTaskStatus] = useState(true);

  const navigate = useNavigate();

  const [locationsFetched, setLocationsFetched] = useState(false);

  const fetchLocations = async () => {
    try {
      const response = await API.get('/get_locations', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setLocations(response.data.locations || []);
      setLocationsFetched(true);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchTasks = useCallback(async () => {
    try {
      const response = await API.get('/get_all_tasks_with_locations', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setTasks(response.data.tasks);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (locationsFetched) {
      fetchTasks();
    }
  }, [locationsFetched, fetchTasks]);

  useEffect(() => {
    if (createMessage) {
      const timer = setTimeout(() => setCreateMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [createMessage]);

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!taskName.trim()) {
      setCreateMessage('Task name is required.');
      return;
    }
    if (!startTime || !endTime) {
      setCreateMessage('Start and End times are required.');
      return;
    }
    try {
      await API.post('/create_task', { 
        name: taskName, 
        isactive: isActive, 
        location_ids: selectedLocations,
        start_time: startTime,
        end_time: endTime 
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      setCreateMessage('Task created successfully');
      setTaskName('');
      setIsActive(true);
      setStartTime('');
      setEndTime('');
      setSelectedLocations([]);
      fetchTasks();
    } catch (error) {
      console.error('Error creating task:', error);
      setCreateMessage('Error creating task. Please try again.');
    }
  };

  const handleEditTask = (task) => {
    setEditingTaskId(task.id);
    setEditingTaskName(task.name);
    setEditingTaskStatus(task.isactive);
  };

  const handleSaveTaskEdit = async () => {
    try {
      await API.put(`/update_task/${editingTaskId}`, {
        name: editingTaskName,
        isactive: editingTaskStatus
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      setEditingTaskId(null);
      fetchTasks();
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingTaskId(null);
  };

  const handleLocationSelect = (locationId) => {
    setSelectedLocations((prevSelected) =>
      prevSelected.includes(locationId)
        ? prevSelected.filter((id) => id !== locationId)
        : [...prevSelected, locationId]
    );
  };

  const filteredLocations = locations
    .filter(location => location.name.toLowerCase().includes(locationSearchTerm.toLowerCase()))
    .slice((locationPage - 1) * locationsPerPage, locationPage * locationsPerPage);

  const filteredTasks = tasks
    .filter((task) => task.name.toLowerCase().includes(taskSearchTerm.toLowerCase()))
    .slice((taskPage - 1) * tasksPerPage, taskPage * tasksPerPage);

  const paginate = (pageSetter, page, items, itemsPerPage) => (
    Array.from({ length: Math.ceil(items.length / itemsPerPage) }, (_, index) => (
      <button
        key={index + 1}
        onClick={() => pageSetter(index + 1)}
        style={styles.pageButton}
        className={page === index + 1 ? 'active' : ''}
      >
        {index + 1}
      </button>
    ))
  );

  return (
    <div style={styles.container}>
      <div style={styles.buttonContainer}>
        <button type="button" onClick={() => navigate('/home')} style={styles.cancelButton}>
          Ana Sayfa
        </button>
      </div>

      <div style={styles.tabHeader}>
        <button
          style={activeTab === 'createTask' ? styles.activeTab : styles.tab}
          onClick={() => setActiveTab('createTask')}
        >
          Görev Tanımla
        </button>
        <button
          style={activeTab === 'existingTasks' ? styles.activeTab : styles.tab}
          onClick={() => setActiveTab('existingTasks')}
        >
          Mevcut Görevler
        </button>
      </div>

      <div style={styles.tabContent}>
        {activeTab === 'createTask' && (
          <div>
            {createMessage && <p style={styles.message}>{createMessage}</p>}
            <form onSubmit={handleCreateTask} style={styles.form}>
              <div style={styles.row}>
                <input
                  type="text"
                  placeholder="Görev Adı"
                  value={taskName}
                  onChange={(e) => setTaskName(e.target.value)}
                  required
                  style={styles.input}
                />
                <select
                  value={isActive.toString()}
                  onChange={(e) => setIsActive(e.target.value === 'true')}
                  style={styles.input}
                >
                  <option value="true">Active</option>
                </select>
              </div>
              <div style={styles.row}>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  required
                  style={styles.input}
                  placeholder="Start Time"
                />
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  required
                  style={styles.input}
                  placeholder="End Time"
                />
              </div>

              <h3>Lokasyon Seçimi:</h3>
              <input
                type="text"
                placeholder="Ara ..."
                value={locationSearchTerm}
                onChange={(e) => setLocationSearchTerm(e.target.value)}
                style={{ width: '100%', padding: '10px', marginBottom: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Seç</th>
                    <th style={styles.th}>Lokasyon Adı</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLocations.map((location) => (
                    <tr key={location.id}>
                      <td style={styles.td}>
                        <input
                          type="checkbox"
                          checked={selectedLocations.includes(location.id)}
                          onChange={() => handleLocationSelect(location.id)}
                        />
                      </td>
                      <td style={styles.td}>{location.name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={styles.pagination}>
                {paginate(setLocationPage, locationPage, locations, locationsPerPage)}
              </div>
              <div style={styles.buttonContainer}>
                <button type="submit" style={styles.createButton}>Kaydet</button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'existingTasks' && (
          <div>
            <input
              type="text"
              placeholder="Ara..."
              value={taskSearchTerm}
              onChange={(e) => setTaskSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Görev Adı</th>
                  <th style={styles.th}>Durum</th>
                  <th style={styles.th}>Başlangıç</th>
                  <th style={styles.th}>Bitiş</th>
                  <th style={styles.th}>Lokasyonlar</th>
                  <th style={styles.th}>-</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map((task) => (
                  <tr key={task.id}>
                    <td style={styles.td}>
                      {editingTaskId === task.id ? (
                        <input
                          type="text"
                          value={editingTaskName}
                          onChange={(e) => setEditingTaskName(e.target.value)}
                          style={styles.input}
                        />
                      ) : (
                        task.name
                      )}
                    </td>
                    <td style={styles.td}>
                      {editingTaskId === task.id ? (
                        <select
                          value={editingTaskStatus}
                          onChange={(e) => setEditingTaskStatus(e.target.value === 'true')}
                          style={styles.input}
                        >
                          <option value="true">Active</option>
                        </select>
                      ) : (
                        task.isactive ? "Active" : "Inactive"
                      )}
                    </td>
                    <td style={styles.td}>{task.start_time}</td>
                    <td style={styles.td}>{task.end_time}</td>
                    <td style={styles.td}>{task.locations.join(', ')}</td>
                    <td style={styles.td}>
                      {editingTaskId === task.id ? (
                        <>
                          <button onClick={handleSaveTaskEdit} style={styles.saveButton}>Kaydet</button>
                          <button onClick={handleCancelEdit} style={styles.cancelButton}>İptal</button>
                        </>
                      ) : (
                        <button onClick={() => handleEditTask(task)} style={styles.editButton}>Düzenle</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={styles.pagination}>
              {paginate(setTaskPage, taskPage, tasks, tasksPerPage)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


const styles = {
  container: {
    padding: '20px',
    maxWidth: '800px',
    margin: 'auto',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    fontFamily: "'Arial', sans-serif",
  },
  tabHeader: {
    display: 'flex',
    borderBottom: '2px solid #ddd',
    marginBottom: '20px',
  },
  tab: {
    flex: 1,
    padding: '10px',
    cursor: 'pointer',
    textAlign: 'center',
    backgroundColor: '#f0f0f0',
    border: 'none',
    fontSize: '10px',
    color: '#333',
    fontWeight: 'bold',
    borderRadius: '4px 4px 0 0',
  },
  activeTab: {
    flex: 1,
    padding: '10px',
    cursor: 'pointer',
    textAlign: 'center',
    backgroundColor: '#ffffff',
    color: '#007bff',
    fontWeight: 'bold',
    borderBottom: '2px solid #007bff',
    borderRadius: '4px 4px 0 0',
  },
  tabContent: {
    padding: '20px',
    backgroundColor: '#f9f9f9',
    borderRadius: '0 0 8px 8px',
  },
  searchInput: {
    width: '100%',
    padding: '10px',
    marginBottom: '15px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    fontSize: '10px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '10px',
  },
  th: {
    padding: '12px',
    borderBottom: '2px solid #ddd',
    fontWeight: 'bold',
    textAlign: 'left',
    backgroundColor: '#f2f2f2',
    color: '#333',
  },
  td: {
    padding: '10px',
    borderBottom: '1px solid #ddd',
    color: '#333',
    textAlign: 'left',
  },
  input: {
    width: '100%',
    padding: '8px',
    fontSize: '10px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    boxSizing: 'border-box',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  row: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },

  buttonContainer: {
    marginBottom: '10px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  
  cancelButton: {
    padding: '10px 20px',
    fontSize: '10px',
    color: '#fff',
    backgroundColor: '#f44336',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  },

  createButton: {
    padding: '12px 20px',
    fontSize: '10px',
    backgroundColor: '#4CAF50',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',

  },

  editButton: {
    padding: '8px 12px',
    backgroundColor: '#007bff',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  },
  saveButton: {
    padding: '8px 12px',
    backgroundColor: '#4CAF50',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  },

  pagination: {
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
    marginTop: '20px',
  },
  pageButton: {
    padding: '8px 12px',
    fontSize: '10px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  },

  message: {
    fontSize: '10px',
    color: '#4CAF50',
    textAlign: 'center',
    marginBottom: '15px',
  },
};

export default CreateTask;
